/**
 * API Plugin
 *
 * Creates a custom $fetch instance with authentication headers and automatic request cancellation.
 * This plugin provides a centralized API client that:
 * - Automatically injects auth tokens from localStorage
 * - Handles 403 errors with custom error code navigation
 * - Manages AbortControllers for all requests
 * - Automatically cancels pending requests on page navigation
 *
 * @usage
 *  For declarative/reactive data fetching (GET on mount)
 * const { data, error } = await useAcvFetch('/api/transport-requests/staged')
 *
 * @usage
 * For imperative calls (POST, PUT, DELETE triggered by user actions)
 * const { $api } = useNuxtApp()
 * const data = await $api('/api/transport-requests/stageJob', {
 *   method: 'POST',
 *   body: payload
 * })
 */
import { useHandleErrorCodes } from '@/composables/useHandleErrorCodes';
import { getAuthTokens } from '@/utilities/authTokens';

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const router = useRouter();

  /**
   * Map to track all active AbortControllers (signal → controller)
   * Used to cancel pending requests when navigating away from a page
   * Prevents memory leaks and race conditions from abandoned requests
   * Key: AbortSignal, Value: AbortController
   */
  const activeRequests = new Map();

  /**
   * Custom $fetch instance configured with base URL and auth headers
   * All requests made through this instance will:
   * - Use the BASE_API from runtime config
   * - Include Authorization and Authorization-Magic-Link headers
   * - Be automatically tracked and cancellable
   */
  const api = $fetch.create({
    baseURL: config.public.BASE_API,

    /**
     * onRequest hook - Executed before each request
     *
     * Responsibilities:
     * 1. Retrieves auth tokens from localStorage
     * 2. Injects Authorization headers into request
     * 3. Creates and tracks AbortController for request cancellation
     *
     * @param {Object} context - Request context
     * @param {Object} context.options - Request options that can be modified
     */
    onRequest({ options }) {
      const { accessToken: token, magicLinkToken } = getAuthTokens();
      if (token) {
        options.headers.set('Authorization', `Bearer ${token}`);
      }

      if (magicLinkToken) {
        options.headers.set('Authorization-Magic-Link', magicLinkToken);
      }

      /**
       * Auto-create AbortController if caller didn't provide one
       * This enables automatic request cancellation on page navigation
       * The controller is tracked in activeRequests Map for cleanup
       */
      if (!options.signal) {
        const controller = new AbortController();
        options.signal = controller.signal;
        activeRequests.set(controller.signal, controller);
      }
    },

    /**
     * onResponse hook - Executed after successful response
     *
     * Cleans up the AbortController for completed requests
     * Prevents memory leaks by removing controllers from tracking Map
     *
     * @param {Object} context - Response context
     * @param {Object} context.options - Request options containing the signal
     */
    onResponse({ options }) {
      if (options.signal) {
        activeRequests.delete(options.signal);
      }
    },

    /**
     * onResponseError hook - Executed when request fails
     *
     * Responsibilities:
     * 1. Cleans up AbortController for failed requests
     * 2. Handles 403 Forbidden errors with custom error codes
     * 3. Navigates user to appropriate error page based on error code
     *
     * @param {Object} context - Error context
     * @param {Object} context.response - Response object containing error details
     * @param {Object} context.options - Request options containing the signal
     */
    async onResponseError({ response, options }) {
      // Clean up AbortController for failed request
      if (options.signal) {
        activeRequests.delete(options.signal);
      }

      // Handle 403 errors with custom error codes
      // Example: Restricted access to assigned jobs, verification failures, etc.
      const errorCode = response._data?.errors?.[0]?.errorCode;
      if (response.status === 403 && errorCode) {
        await nuxtApp.runWithContext(() => {
          const { handleNavigationByErrorCode } = useHandleErrorCodes();
          handleNavigationByErrorCode(errorCode);
        });
      }
    },
  });

  /**
   * Helper function to abort all pending requests
   * Shared by router navigation guard and manual cancellation
   *
   * @param {string} reason - Reason for aborting requests (helps identify cancellation source in error logs)
   */
  const cancelAllPendingRequests = (reason = 'Request cancelled') => {
    if (activeRequests.size > 0) {
      activeRequests.forEach((controller) => {
        controller.abort(reason);
      });
      activeRequests.clear();
    }
  };

  /**
   * Router navigation guard
   *
   * Aborts all pending API requests when user navigates to a different page
   * This prevents:
   * - Memory leaks from abandoned requests
   * - Race conditions where old page data updates after navigation
   * - Unnecessary network traffic for requests no longer needed
   *
   * Called before each route change, including:
   * - User clicking links
   * - programmatic navigation (navigateTo, router.push)
   * - Browser back/forward buttons
   *
   * Special handling for /check-email route:
   * Ensures all API calls are cancelled when redirecting to the login/auth flow
   */
  router.beforeEach(() => {
    cancelAllPendingRequests('Navigation: request cancelled due to route change');
  });

  /**
   * Provide the API client to the Nuxt app
   * Makes $api available throughout the application via useNuxtApp()
   *
   * @returns {Object} Plugin providers
   * @returns {Function} api - The custom $fetch instance with auth and cancellation
   * @returns {Function} cancelAllRequests - Helper to manually cancel all pending requests
   */
  return {
    provide: {
      api,
      cancelAllRequests: cancelAllPendingRequests,
    },
  };
});
